// Where else the Bible says this. OpenBible ranks the Treasury of Scripture
// Knowledge's links by reader vote, so the best-attested come first and a short
// list is worth reading rather than a wall of references.
//
// The preview is public-domain text, named as such: the ESV cannot be quoted
// here, because 12 cross-references on every verse of every chapter is not a
// cache of 450 verses.
import { parseRef, toSlug, type TranslationId } from "@bibleunleashed/shared";
import { useState } from "react";
import { Link } from "wouter";
import { track } from "@/lib/events";
import { useCrossRefs } from "@/lib/study";
import { RailCard } from "./RailCard";

const FIRST = 6;

export function CrossRefsCard({ verse, translation }: { verse: string | null; translation: TranslationId }) {
  const [expanded, setExpanded] = useState(false);
  const { data, isLoading, error } = useCrossRefs(verse ?? undefined, translation);
  const refs = expanded ? data?.refs ?? [] : (data?.refs ?? []).slice(0, FIRST);

  return (
    <RailCard title="Cross-references" verse={data?.display}>
      {!verse && <Empty>Click a verse to see where else the Bible says it.</Empty>}
      {verse && isLoading && <Empty>Looking…</Empty>}
      {error && <Empty>Couldn’t load cross-references.</Empty>}
      {verse && data && !data.refs.length && <Empty>Nothing is cross-referenced to this verse.</Empty>}

      {refs.length > 0 && (
        <ul className="space-y-3">
          {refs.map((entry) => (
            <li key={entry.target}>
              {entry.written ? (
                <Link
                  href={`/commentary/${toSlug(parseRef(entry.target))}#v${parseRef(entry.target).verse ?? 1}`}
                  onClick={() => track({ type: "study_crossrefs", ref: verse ?? undefined, detail: `open ${entry.display}` })}
                  className="font-sans text-[11.5px] font-medium tracking-[0.04em] text-accent hover:underline"
                >
                  {entry.display}
                </Link>
              ) : (
                <span className="font-sans text-[11.5px] font-medium tracking-[0.04em] text-ink-2">{entry.display}</span>
              )}
              <p className="mt-0.5 font-serif text-[14px] leading-snug text-ink-2">
                {entry.text || "—"}
                {entry.truncated && <span className="text-ink-3"> …</span>}
              </p>
            </li>
          ))}
        </ul>
      )}

      {data && data.refs.length > FIRST && (
        <button
          onClick={() => {
            if (!expanded) track({ type: "study_crossrefs", ref: verse ?? undefined, detail: "show all" });
            setExpanded((v) => !v);
          }}
          className="mt-3 font-sans text-[12px] text-ink-3 hover:text-accent"
        >
          {expanded ? "Show fewer" : `Show all ${data.refs.length}`}
          {!expanded && data.total > data.refs.length ? ` of ${data.total}` : ""}
        </button>
      )}
      {refs.length > 0 && (
        <p className="mt-3 font-sans text-[10.5px] text-ink-3">
          {data!.translation} ·{" "}
          <a href="https://www.openbible.info/labs/cross-references/" target="_blank" rel="noopener noreferrer" className="hover:text-accent">
            openbible.info
          </a>
        </p>
      )}
    </RailCard>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="font-serif text-[14.5px] leading-relaxed text-ink-3 italic">{children}</p>;
}
